import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../css/featureDetail.css";

const AdvantagePage = ({ tag, title, desc, icon, highlights, steps, ctaBack = "/" }) => (
  <>
    <Helmet>
      <title>{title} — Salary Uncle</title>
      <meta name="description" content={desc} />
    </Helmet>

    <section className="fd-hero">
      <div className="fd-hero-inner">
        <Link to={ctaBack} className="fd-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Home
        </Link>
        <div className="fd-hero-icon">{icon}</div>
        <span className="fd-tag">{tag}</span>
        <h1>{title}</h1>
        <p>{desc}</p>
      </div>
    </section>

    <section className="fd-body">
      <div className="fd-body-inner">
        <div className="fd-highlights">
          {highlights.map((h, i) => (
            <div className="fd-highlight-card" key={i}>
              <div className="fd-hl-num">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <div className="fd-hl-title">{h.title}</div>
                <div className="fd-hl-desc">{h.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="fd-steps-box">
          <h2>How It Works</h2>
          <div className="fd-steps">
            {steps.map((s, i) => (
              <div className="fd-step-row" key={i}>
                <div className="fd-step-dot">{s.step}</div>
                <div className="fd-step-text">{s.text}</div>
              </div>
            ))}
          </div>
          <Link to="/apply-now" className="fd-cta-btn">Apply Now</Link>
        </div>
      </div>
    </section>
  </>
);

export const AdvantageInstantApproval = () => (
  <AdvantagePage
    tag="LIGHTNING FAST"
    title="Instant Approval"
    desc="AI-powered loan decisions in under 60 seconds — no manual review, no waiting in queues."
    icon={<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
    highlights={[
      { title: "AI Eligibility Engine", desc: "Real-time evaluation of your salary, employment, and credit profile." },
      { title: "60-Second Decision", desc: "Get a loan decision while you wait — no calls, no follow-ups." },
      { title: "Instant Document Verification", desc: "Auto-extracts data from your Aadhaar, PAN, and salary slip." },
      { title: "Pre-Approved Offers", desc: "Returning customers see ready-to-disburse offers in one tap." },
      { title: "24/7 Application", desc: "Apply anytime — our system works round the clock, including holidays." },
      { title: "Direct Bank Transfer", desc: "Funds disbursed straight to your bank account within minutes of approval." },
    ]}
    steps={[
      { step: "1", text: "Enter your mobile number and verify with OTP." },
      { step: "2", text: "Fill in basic details — name, PAN, salary." },
      { step: "3", text: "AI evaluates and approves in 60 seconds." },
      { step: "4", text: "Accept the offer and complete eKYC." },
      { step: "5", text: "Funds disbursed to your bank account instantly." },
    ]}
  />
);

export const AdvantageBankGradeSecurity = () => (
  <AdvantagePage
    tag="SECURITY FIRST"
    title="Bank-Grade Security"
    desc="256-bit SSL encryption protects every transaction and document. Your data is safer than your bank."
    icon={<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>}
    highlights={[
      { title: "256-bit SSL Encryption", desc: "All data exchanges secured with industry-standard encryption." },
      { title: "Encrypted Document Storage", desc: "Your PAN, Aadhaar, and salary slips are stored encrypted at rest." },
      { title: "No Data Sharing", desc: "We never sell, rent, or share your data with third parties without consent." },
      { title: "RBI-Compliant Practices", desc: "Data handling aligned with RBI's data protection guidelines." },
      { title: "Two-Factor Authentication", desc: "OTP-based login keeps unauthorised users out." },
      { title: "Regular Security Audits", desc: "Independent security audits performed every quarter." },
    ]}
    steps={[
      { step: "1", text: "All transactions encrypted end-to-end with SSL/TLS." },
      { step: "2", text: "Documents stored on encrypted servers with restricted access." },
      { step: "3", text: "OTP verification required for every login." },
      { step: "4", text: "Customer data never shared without explicit consent." },
      { step: "5", text: "Right to delete your data on request — anytime." },
    ]}
  />
);

export const AdvantageLowestInterestRates = () => (
  <AdvantagePage
    tag="TRANSPARENT PRICING"
    title="Lowest Interest Rates"
    desc="Starting at just 10.5% per annum — no hidden charges, no surprises. What you see is what you pay."
    icon={<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
    highlights={[
      { title: "From 10.5% p.a.", desc: "Industry-competitive rates for salaried professionals." },
      { title: "Zero Hidden Charges", desc: "All fees disclosed upfront before you accept any loan." },
      { title: "Profile-Based Pricing", desc: "Better credit profile? Get even lower rates automatically." },
      { title: "Zero Prepayment Penalty", desc: "Pay off your loan early at any time — no extra charges." },
      { title: "Transparent Calculator", desc: "Use our calculator to see exactly what you'll pay." },
      { title: "Flexible Tenure", desc: "Choose the tenure that gives you the lowest EMI you can afford." },
    ]}
    steps={[
      { step: "1", text: "Apply and get your personalised interest rate." },
      { step: "2", text: "Review the loan agreement — all charges clearly shown." },
      { step: "3", text: "Use our calculator to plan your repayment." },
      { step: "4", text: "Accept and receive funds at the disclosed rate." },
      { step: "5", text: "Repay on time — no penalties, no surprises." },
    ]}
  />
);

export const AdvantageSameDayDisbursement = () => (
  <AdvantagePage
    tag="FAST FUNDS"
    title="Same Day Disbursement"
    desc="Loan credited to your account within 4 hours of approval — often in just 30 minutes."
    icon={<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
    highlights={[
      { title: "30-Minute Average", desc: "Most loans disbursed within 30 minutes of final approval." },
      { title: "IMPS/NEFT Transfer", desc: "Direct bank transfer using fastest payment rails." },
      { title: "All Major Banks", desc: "Works with SBI, HDFC, ICICI, Axis, PNB and 100+ more." },
      { title: "Live Status Tracking", desc: "Track every step via SMS, email, and your dashboard." },
      { title: "24/7 Disbursement", desc: "Funds released round the clock, including weekends." },
      { title: "No Branch Visits", desc: "Everything happens digitally — never visit an office." },
    ]}
    steps={[
      { step: "1", text: "Loan offer accepted and eKYC completed." },
      { step: "2", text: "Bank account verified via penny-drop check." },
      { step: "3", text: "Disbursement triggered automatically." },
      { step: "4", text: "Funds credited via IMPS within 30 minutes." },
      { step: "5", text: "SMS and email confirmation sent instantly." },
    ]}
  />
);

export const AdvantageNoCollateral = () => (
  <AdvantagePage
    tag="UNSECURED LENDING"
    title="No Collateral Required"
    desc="100% unsecured loans — no assets, no guarantors, no security required. Your salary is enough."
    icon={<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h14v4"/><path d="M4 6v12a2 2 0 0 0 2 2h14v-4"/><circle cx="16" cy="14" r="1" fill="#fff"/></svg>}
    highlights={[
      { title: "No Asset Pledge", desc: "Never pledge gold, property, or any valuable as security." },
      { title: "No Guarantor Needed", desc: "No one else has to sign for you — your salary qualifies you." },
      { title: "Salary-Based Eligibility", desc: "Eligibility is purely based on your monthly income." },
      { title: "Quick Approval", desc: "No asset valuation delays — approval happens instantly." },
      { title: "Stress-Free", desc: "Your personal belongings stay yours — even during the loan." },
      { title: "Fully Digital", desc: "100% online process — no paperwork for collateral." },
    ]}
    steps={[
      { step: "1", text: "Apply with mobile number and OTP." },
      { step: "2", text: "Share your salary slip and bank statement." },
      { step: "3", text: "AI checks your income and approves the loan." },
      { step: "4", text: "Accept terms — no security or guarantor needed." },
      { step: "5", text: "Funds disbursed to your account immediately." },
    ]}
  />
);
